import { useEffect, useMemo, useState } from "react";
import { Paragraph, QuestionMarkCircleIcon, RichText } from "@datagame/ui";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import { Section } from "../../Common";
import { useGame } from "../../../../../../../contexts/game/GameContext";
import { RANKIFIER_CONFIGURATION_UPDATE } from "../../../../../../../graphql/mutations/GameMutation";
import type { Game, RankifierConfigurationUpdateInput } from "../../../../../../../gql/graphql";
import { SelectInput } from "../../../../../../shared/ui/SelectInput";
import type { SelectOption } from "../../../../../../shared/ui/SelectInput/SelectInput";
import { AttributesSection } from "../AttributesSection";
import { TooltipWithIcon } from "../../../../../../shared/ui/TooltipWithIcon";

import styles from "./RankifierSettingsForm.module.scss";

export interface RankifierSettingsFormProps {
  game: Game;
}

export const RankifierSettingsForm = ({ game }: RankifierSettingsFormProps) => {
  const { t } = useTranslation();

  const cardsPerHandOptions = useMemo(() => {
    return [
      {
        value: "3",
        label: "3",
      },
      {
        value: "4",
        label: "4",
      },
      {
        value: "5",
        label: "5",
      },
      {
        value: "6",
        label: "6",
      },
    ] as SelectOption[];
  }, []);

  const minimumAttributesDisplayOptions = useMemo(() => {
    return [
      {
        value: "1",
        label: "1",
      },
      {
        value: "2",
        label: "2",
      },
      {
        value: "3",
        label: "3",
      },
      {
        value: "4",
        label: "4",
      },
      {
        value: "5",
        label: "5",
      },
      {
        value: "6",
        label: "6",
      },
      {
        value: "7",
        label: "7",
      },
      {
        value: "8",
        label: "8",
      },
    ] as SelectOption[];
  }, []);

  const { currentAttributes } = useGame();
  const [mostQuestion, setMostQuestion] = useState<string | undefined>();
  const [leastQuestion, setLeastQuestion] = useState<string | undefined>();

  const [cardsPerHand, setCardsPerHand] = useState(
    cardsPerHandOptions.find((item) => {
      if ("cardsPerHand" in game.configuration) {
        return game.configuration.cardsPerHand === parseInt(item.value);
      }
    }),
  );

  const [minimumToDisplay, setMinimumToDisplay] = useState(
    minimumAttributesDisplayOptions.find((item) => {
      if ("numberOfRounds" in game.configuration) {
        return game.configuration.numberOfRounds === parseInt(item.value);
      }
    }),
  );

  const [updateConfigMutation] = useMutation(RANKIFIER_CONFIGURATION_UPDATE);

  useEffect(() => {
    setMostQuestion("mostQuestion" in game.configuration ? game.configuration.mostQuestion : undefined);
    setLeastQuestion("leastQuestion" in game.configuration ? game.configuration.leastQuestion : undefined);
  }, []);

  const handleMostQuestionChange = (value: string) => {
    setMostQuestion(value);
  };

  const handleLeastQuestionChange = (value: string) => {
    setLeastQuestion(value);
  };

  const updateConfig = (data: RankifierConfigurationUpdateInput) => {
    updateConfigMutation({
      variables: {
        rankifierConfigurationId: game.configuration.id,
        data,
      },
    });
  };

  const handleCardsPerHandChange = (item: SelectOption) => {
    const newValue = cardsPerHandOptions.find((option) => {
      return option.value === item.value;
    });

    setCardsPerHand(newValue);

    newValue?.value && updateConfig({ cardsPerHand: parseInt(newValue.value) });
  };

  const handleMinimumToDisplayChange = (item: SelectOption) => {
    const newValue = minimumAttributesDisplayOptions.find((option) => {
      return option.value === item.value;
    });
    setMinimumToDisplay(newValue);

    newValue?.value && updateConfig({ numberOfRounds: parseInt(newValue.value) });
  };

  const handleSave = () => {
    updateConfig({ mostQuestion, leastQuestion });
  };

  return (
    <>
      <Section
        title={t("rankifier:mostQuestion")}
        tooltip={<TooltipWithIcon content={t("rankifier:mostQuestionTooltip")} />}
      >
        <RichText
          icon={<QuestionMarkCircleIcon />}
          placeholder={t("rankifier:mostQuestionPlaceholder") ?? ""}
          value={mostQuestion || ""}
          onChange={handleMostQuestionChange}
          onBlur={handleSave}
          size="small"
          className={styles.input}
        />
      </Section>

      <Section
        title={t("rankifier:leastQuestion")}
        tooltip={<TooltipWithIcon content={t("rankifier:leastQuestionTooltip")} />}
      >
        <RichText
          icon={<QuestionMarkCircleIcon />}
          placeholder={t("rankifier:leastQuestionPlaceholder") ?? ""}
          value={leastQuestion}
          onChange={handleLeastQuestionChange}
          onBlur={handleSave}
          size="small"
          className={styles.input}
        />
      </Section>

      <AttributesSection game={game} />

      <Section
        title={t("rankifier:gameplayRules")}
        headerClassName={styles.gameplaySectionHeader}
        className={styles.gampeplaySection}
      >
        <Paragraph>{t("rankifier:howToCalculate")}</Paragraph>
        <Paragraph>
          <strong>{t("rankifier:formula")}</strong>
        </Paragraph>
        <Section
          title={t("rankifier:cardsPerHand")}
          tooltip={<TooltipWithIcon content={t("rankifier:cardsPerHandTooltip")} />}
        >
          <SelectInput
            options={cardsPerHandOptions}
            selected={cardsPerHand}
            onChange={handleCardsPerHandChange}
            buttonClassName={styles.input}
          />
        </Section>

        <Section
          title={t("rankifier:minimumToDisplay")}
          tooltip={<TooltipWithIcon content={t("rankifier:minimumToDisplayTooltip")} />}
          tip={
            <Trans
              i18nKey="rankifier:currentCalculation"
              count={Math.ceil(
                (currentAttributes.length * parseInt(minimumToDisplay?.value ?? "")) /
                  parseInt(cardsPerHand?.value ?? ""),
              )}
            />
          }
        >
          <SelectInput
            options={minimumAttributesDisplayOptions}
            selected={minimumToDisplay}
            onChange={handleMinimumToDisplayChange}
            buttonClassName={styles.input}
          />
        </Section>
      </Section>
    </>
  );
};
